import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronDown, CalendarClock, Search, X, Clock, ArrowRight,
} from 'lucide-react';
import { schedulerService } from '../../../../services/schedulerService';
import { useSeller } from '../../../../context/SellerContext';
import toast from 'react-hot-toast';

const TYPE_CONFIG = {
  DAILY_ORDER_SALES:        { label: 'Daily Sales',        dot: 'bg-[#232F3E]',   bg: 'bg-[#FFF4E6]', text: 'text-[#C45500]', border: 'border-[#FFD89E]' },
  PRODUCT_LISTING_STATUS:   { label: 'Listing Health',     dot: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  FEEDBACK_REVIEW:          { label: 'Feedback & Review',  dot: 'bg-amber-500',   bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
  PRODUCT_HIJACK:           { label: 'Hijack Alert',       dot: 'bg-red-500',     bg: 'bg-red-50',     text: 'text-red-700',     border: 'border-red-200' },
  POLICY_WARNING:           { label: 'Policy Warning',     dot: 'bg-orange-500',  bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200' },
  REVIEW_REQUEST:           { label: 'Review Request',     dot: 'bg-violet-500',  bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200' },
  SPONSORED_AD_PERFORMANCE: { label: 'Sponsored Ads',      dot: 'bg-pink-500',    bg: 'bg-pink-50',    text: 'text-pink-700',    border: 'border-pink-200' },
};

const FREQ_LABELS = {
  every_1_hour: 'Every hour', every_2_hours: 'Every 2h', every_4_hours: 'Every 4h',
  every_6_hours: 'Every 6h', daily: 'Daily', weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly',
};

function formatDt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function formatTimeOfDay(scheduleTime, cron) {
  if (scheduleTime) return scheduleTime;
  if (!cron) return '';
  const parts = cron.split(' ');
  if (parts.length === 6) {
    const [, min, hour] = parts;
    if (hour !== '*' && min !== '*') return `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
  }
  return '';
}

function getTypeConfig(type) {
  return TYPE_CONFIG[type?.toUpperCase()] || {
    label: type?.replace(/_/g, ' ') || 'Unknown',
    dot: 'bg-slate-500', bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200',
  };
}

// Status of a single schedule
function StatusPill({ s }) {
  if (s.isCancelled) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        Cancelled
      </span>
    );
  }
  if (s.isActive) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
      Inactive
    </span>
  );
}

function SellerSchedulesCard({ group, expanded, onToggle }) {
  const sellerName = (group.sellerName || 'Unknown').trim();
  const initial = sellerName.charAt(0)?.toUpperCase() || 'S';
  const total = group.schedules.length;
  const active = group.schedules.filter(s => s.isActive && !s.isCancelled).length;

  // Find the next upcoming run across this seller's schedules — drives the header summary
  const nextUp = useMemo(() => {
    const now = Date.now();
    let best = null;
    for (const s of group.schedules) {
      if (!s.nextRunAt) continue;
      const t = new Date(s.nextRunAt).getTime();
      if (Number.isNaN(t)) continue;
      if (t < now) continue;
      if (!best || t < best.t) best = { s, t };
    }
    return best?.s || null;
  }, [group.schedules]);

  return (
    <div className="bg-white rounded-lg border border-[#D5D9D9] overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F7FAFA] transition-colors"
      >
        {/* Avatar */}
        <div className="w-9 h-9 rounded-md bg-[#232F3E] flex items-center justify-center text-[#f08804] text-xs font-bold flex-shrink-0">
          {initial}
        </div>

        {/* Name + summary */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-[13px] font-semibold text-[#0F1111] truncate">{sellerName}</h3>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex-shrink-0">
              {active}/{total} active
            </span>
          </div>
          {nextUp ? (
            <div className="flex items-center gap-1.5 text-[11px] text-[#565959] truncate">
              <Clock size={11} className="text-[#999] flex-shrink-0" />
              <span>Next:</span>
              <span className="font-medium text-[#0F1111]">{getTypeConfig(nextUp.notificationType).label}</span>
              <ArrowRight size={10} className="text-[#999] flex-shrink-0" />
              <span className="text-[#C45500] font-medium">{formatDt(nextUp.nextRunAt)}</span>
            </div>
          ) : (
            <div className="text-[11px] text-[#999] italic">No upcoming runs scheduled</div>
          )}
        </div>

        {/* Chevron */}
        <ChevronDown
          size={16}
          className={`text-[#999] transition-transform flex-shrink-0 ${expanded ? 'rotate-0' : '-rotate-90'}`}
        />
      </button>

      {/* Body — schedule list */}
      {expanded && (
        <div className="border-t border-[#E7E7E7] overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="bg-[#F7F7F7] border-b border-[#E7E7E7]">
                <th className="text-left text-[10px] font-semibold text-[#565959] uppercase tracking-wider py-2 px-4">Notification</th>
                <th className="text-left text-[10px] font-semibold text-[#565959] uppercase tracking-wider py-2 px-4">Frequency</th>
                <th className="text-left text-[10px] font-semibold text-[#565959] uppercase tracking-wider py-2 px-4">Time</th>
                <th className="text-left text-[10px] font-semibold text-[#565959] uppercase tracking-wider py-2 px-4">Last Run</th>
                <th className="text-left text-[10px] font-semibold text-[#565959] uppercase tracking-wider py-2 px-4">Next Run</th>
                <th className="text-left text-[10px] font-semibold text-[#565959] uppercase tracking-wider py-2 px-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {group.schedules.map((s, idx) => {
                const tc = getTypeConfig(s.notificationType);
                const time = formatTimeOfDay(s.scheduleTime, s.cronExpression);
                return (
                  <tr key={s.taskKey || idx} className="border-b border-[#F1F1F1] last:border-0 hover:bg-[#F7FAFA] transition-colors">
                    <td className="py-2 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${tc.bg} ${tc.text} ${tc.border}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${tc.dot}`} />
                        {tc.label}
                      </span>
                    </td>
                    <td className="py-2 px-4">
                      <span className="text-[12px] text-[#565959]">{FREQ_LABELS[s.scheduleFrequency] || s.scheduleFrequency || '—'}</span>
                    </td>
                    <td className="py-2 px-4">
                      <span className="text-[12px] text-[#565959] font-mono">{time || '—'}</span>
                    </td>
                    <td className="py-2 px-4">
                      <span className="text-[11px] text-[#565959]">{formatDt(s.lastRunAt)}</span>
                    </td>
                    <td className="py-2 px-4">
                      <span className="text-[11px] text-[#C45500] font-medium">{formatDt(s.nextRunAt)}</span>
                    </td>
                    <td className="py-2 px-4">
                      <StatusPill s={s} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function SchedulerStatus({ renderActions, onPaginationChange }) {
  const { selectedSeller } = useSeller();
  const sellerId = selectedSeller?.id || '';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(() => new Set()); // sellerIds that are open

  const fetchStatus = useCallback(async () => {
    try {
      const data = await schedulerService.getStatus(sellerId);
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch scheduler status:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await schedulerService.refresh();
      setStatus(data);
      toast.success(`Refreshed — ${data?.activeTaskCount || 0} active tasks`);
    } catch {
      toast.error('Failed to refresh schedules');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (renderActions) renderActions({ onRefresh: handleRefresh, refreshing });
  }, [renderActions, handleRefresh, refreshing]);

  const schedules = status?.schedules || [];

  // Group schedules by seller, sort alphabetically
  const sellers = useMemo(() => {
    const map = new Map();
    schedules.forEach(s => {
      const sId = s.sellerId || 'unknown';
      if (!map.has(sId)) {
        map.set(sId, { sellerId: sId, sellerName: (s.sellerName || 'Unknown').trim(), schedules: [] });
      }
      map.get(sId).schedules.push(s);
    });
    const arr = Array.from(map.values());
    arr.sort((a, b) => a.sellerName.localeCompare(b.sellerName));
    return arr;
  }, [schedules]);

  // Apply search filter (seller name)
  const filteredSellers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sellers;
    return sellers.filter(g => g.sellerName.toLowerCase().includes(q));
  }, [sellers, search]);

  // Pagination — by seller, not by schedule
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;
  const totalItems = filteredSellers.length;
  const totalPages = totalItems === 0 ? 1 : Math.ceil(totalItems / PAGE_SIZE);
  const paginatedSellers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredSellers.slice(start, start + PAGE_SIZE);
  }, [filteredSellers, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [search]);

  useEffect(() => {
    onPaginationChange?.({ totalPages, currentPage, totalItems, pageSize: PAGE_SIZE, setCurrentPage });
  }, [totalPages, currentPage, totalItems]);

  const toggleOne = useCallback((sId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(sId)) next.delete(sId);
      else next.add(sId);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpanded(new Set(paginatedSellers.map(g => g.sellerId)));
  }, [paginatedSellers]);

  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  const totalActive = useMemo(
    () => schedules.filter(s => s.isActive && !s.isCancelled).length,
    [schedules]
  );

  if (loading) return (
    <div className="space-y-2 p-4">
      {[1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />)}
    </div>
  );

  if (schedules.length === 0) return (
    <div className="flex items-center justify-center py-16">
      <div className="text-center">
        <CalendarClock size={32} className="mx-auto text-slate-300 mb-3" />
        <h3 className="text-base font-semibold text-slate-700">No Active Schedules</h3>
        <p className="text-sm text-slate-400 mt-1">Configure notification schedules to see them here.</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by seller name..."
            className="w-full pl-8 pr-8 py-[7px] border border-[#D5D9D9] rounded-lg text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] placeholder:text-slate-400"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100"
              title="Clear"
            >
              <X size={13} className="text-slate-400" />
            </button>
          )}
        </div>

        <div className="text-[12px] text-[#565959] tabular-nums">
          <span className="font-medium text-[#0F1111]">{filteredSellers.length}</span> seller{filteredSellers.length !== 1 ? 's' : ''}
          <span className="mx-1.5 text-[#CCC]">·</span>
          <span className="font-medium text-[#0F1111]">{totalActive}</span> active schedule{totalActive !== 1 ? 's' : ''}
        </div>

        <div className="flex-1" />

        <button
          onClick={expandAll}
          className="px-2.5 py-[7px] rounded-lg text-[12px] border border-[#D5D9D9] text-[#565959] bg-white hover:border-[#888] hover:text-[#0F1111] transition-colors"
        >
          Expand all
        </button>
        <button
          onClick={collapseAll}
          className="px-2.5 py-[7px] rounded-lg text-[12px] border border-[#D5D9D9] text-[#565959] bg-white hover:border-[#888] hover:text-[#0F1111] transition-colors"
        >
          Collapse all
        </button>
      </div>

      {/* Empty after filter */}
      {filteredSellers.length === 0 ? (
        <div className="flex items-center justify-center py-12 bg-white border border-[#D5D9D9] rounded-lg">
          <div className="text-center">
            <Search size={24} className="mx-auto text-slate-300 mb-2" />
            <p className="text-[13px] font-medium text-slate-600">No sellers match &ldquo;{search}&rdquo;</p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {paginatedSellers.map(group => (
            <SellerSchedulesCard
              key={group.sellerId}
              group={group}
              expanded={expanded.has(group.sellerId)}
              onToggle={() => toggleOne(group.sellerId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
